<template>
  <div class="relative px-8 py-20">
    <div v-if="orders" class="w-full bg-white flex flex-right  py-3" style="color: #414066">
      <h1 class="font-bold m-4" style="font-size: 1.5rem;">Orders</h1>
      <input
          type="text"
          class="admin-input"
          placeholder="Search"
          v-model="filter"
          style="width: 80%;"
        />
    </div>
    <div class="inline-block min-w-full mt-4 bg-white" style="color: #414066">
      <table class="min-w-full divide-y divide-gray-300" style="color: #414066">
        <thead>
          <tr
            v-for="headerGroup in table.getHeaderGroups()"
            :key="headerGroup.id"
          >
            <th
              v-for="header in headerGroup.headers"
              :key="header.id"
              scope="col"
              class="px-3 py-3.5 text-left text-sm font-semibold"
              :class="{
                'cursor-pointer select-none': header.column.getCanSort(),
              }"
              @click="header.column.getToggleSortingHandler()?.($event)"
            >
              <FlexRender
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
              {{ { asc: ' ↑', desc: '↓' }[header.column.getIsSorted()] }}
            </th>
          </tr>
        </thead>

        <tbody class="divide-y divide-gray-200">
          <tr v-for="row in table.getRowModel().rows" :key="row.id">
            <td
            v-for="cell in row.getVisibleCells()"
              :key="cell.id"
              class="whitespace-nowrap px-3 py-4 text-sm text-left pl-4 max-w-[150px] truncate overflow-hidden"
            >   
              <FlexRender
                :render="cell.column.columnDef.cell"
                :props="cell.getContext()"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="p-2 bg-white flex justify-between">
      <div class="p-2 bg-white font-semibold text-left" style="color: #414066; width: fit-content;">
        Page {{ table.getState().pagination.pageIndex + 1 }} of
        {{ table.getPageCount() }} -
        {{ table.getFilteredRowModel().rows.length }} results
      </div>
      <div class="flex justify-center">
        <button
          class="btn btn-outline-light"
          @click="table.setPageIndex(0)"
        >
          ⭰
        </button>
        <button
          class="btn btn-outline-light"
          :disabled="!table.getCanPreviousPage()"
          @click="table.previousPage()"
        >
          ⭠
        </button>
        <button
          class="btn btn-outline-light"
          :disabled="!table.getCanNextPage()"
          @click="table.nextPage()"
        >
          ⭢
        </button>
        <button
          class="btn btn-outline-light"
          @click="table.setPageIndex(table.getPageCount() - 1)"
        >
          ⭲
        </button>
      </div>
      <div class="flex justify-right">
        <button
          class="btn btn-outline-light"
          @click="table.setPageSize(10)"
        >
          10
        </button>
        <button
          class="btn btn-outline-light"
          @click="table.setPageSize(20)"
        >
          20
        </button>
        <button
          class="btn btn-outline-light"
          @click="table.setPageSize(50)"
        >
          50
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import axios from "axios";
import { toast } from 'vue3-toastify';
import { h, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router';
import {
  useVueTable,
  FlexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
} from '@tanstack/vue-table';

const orders = ref([]);
const router = useRouter();
const sorting = ref([{ id: 'id', desc: true }]); // Default sorting: ascending on 'columnId'
const filter = ref('');

onMounted(() => {
  if (!localStorage.getItem("token")) {
    router.push("/login");
  }

  fetchOrders();
}); 

const columns = [
  {
    accessorKey: 'id',
    header: 'ID'
  },
  {
    accessorKey: 'number',
    header: 'Number'
  },
  {
    accessorKey: 'client_address',
    header: 'Address'
  },
  {
    accessorKey: 'amount',
    header: 'Amount'
  },
  {
    accessorKey: 'currency',
    header: 'Currency'
  },
  {
    accessorKey: 'display_text',
    header: 'Display text'
  },
  {
    accessorKey: 'coords_x_left',
    header: 'x left'
  },
  {
    accessorKey: 'coords_x_right',
    header: 'x right'
  },
  {
    accessorKey: 'coords_y_top',
    header: 'y top'
  },
  {
    accessorKey: 'coords_y_bottom',
    header: 'y bot.'
  },
  {
    accessorKey: 'edit',
    header: ' ',
    cell: ({ row }) => 
      h(
        "a",
        {
          href: "#",
          onClick: (event) => {
            event.preventDefault(); // Prevent default anchor behavior
            router.push(`/admin/orders/${row.original.id}`); // Navigate to order form
          },
        },
        "Edit"
      ),
  },
]

const table = useVueTable({
  data: orders,
  columns: columns,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  state: {
    get sorting() {
      return sorting.value
    },
    get globalFilter() {
      return filter.value
    },
  },
  onSortingChange: updaterOrValue => {
    sorting.value =
      typeof updaterOrValue === 'function'
        ? updaterOrValue(sorting.value)
        : updaterOrValue
  },
})

async function fetchOrders() {
  try {
    const token = localStorage.getItem("token"); // Get the token from localStorage

    const response = await axios.get(`${process.env.VUE_APP_API_ADMIN_BASE}/orders`, {
      headers: {
        Authorization: `Bearer ${token}`  // Send token in Authorization header
      }
    });

    if(response.status === 200) {
      if(response.data.orders) {
        orders.value = response.data.orders;
      } else {
        toast.warning('Failed to load orders.\nTry reloading.', { icon: false, });
      }
    }
  } catch (error) {
    if(error.status === 401 || error.status === 403){
      toast.warning('Login for access.', { icon: false, });
    } else {
      toast.error('There was an error while loading orders.\nTry reloading.', { icon: false, })
    }
  }
}
</script>
